'use client'

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PaneListEmptyState,
  PaneListSkeleton,
  candidateOrganisationPath,
} from '@yeshuman/sdk'
import { Building2, EyeOff, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useWatchedOrganisations } from '../../hooks/use-watched-organisations'

type WatchedOrganisationsListProps = {
  onBrowseJobs?: () => void
}

function locationLabel(city: string, country: string): string {
  return [city, country].map((part) => part.trim()).filter(Boolean).join(', ')
}

export function WatchedOrganisationsList({ onBrowseJobs }: WatchedOrganisationsListProps) {
  const { organisations, isLoading, error, unwatch, unwatchingSlug } = useWatchedOrganisations()

  if (isLoading) {
    return <PaneListSkeleton count={3} ariaLabel="Loading watched companies" />
  }

  if (error) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-destructive">Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    )
  }

  if (organisations.length === 0) {
    return (
      <Card className="w-full">
        <CardContent>
          <PaneListEmptyState
            icon={<Building2 aria-hidden />}
            title="No watched companies yet"
            description="Watch a company from its profile to see it here and get notified when they post new jobs."
            action={
              onBrowseJobs ? (
                <Button type="button" variant="secondary" size="sm" onClick={onBrowseJobs}>
                  <Search className="mr-2 h-4 w-4" aria-hidden />
                  Browse jobs
                </Button>
              ) : undefined
            }
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {organisations.map((org) => {
        const place = locationLabel(org.city, org.country)
        const isUnwatching = unwatchingSlug === org.slug
        return (
          <li
            key={org.slug}
            className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <Link
              to={candidateOrganisationPath(org.slug)}
              className="flex min-w-0 items-center gap-3 text-foreground hover:underline"
            >
              {org.logo_url ? (
                <img
                  src={org.logo_url}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-md object-cover"
                />
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                  <Building2 className="h-5 w-5 text-muted-foreground" aria-hidden />
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate font-medium">{org.name}</span>
                {(org.industry || place) && (
                  <span className="block truncate text-sm text-muted-foreground">
                    {[org.industry, place].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
            </Link>
            <Button
              type="button"
              variant="outlinePrimary"
              size="sm"
              disabled={isUnwatching}
              aria-label={`Unwatch ${org.name}`}
              onClick={() => unwatch(org.slug)}
            >
              <EyeOff className="h-4 w-4" aria-hidden />
              Unwatch
            </Button>
          </li>
        )
      })}
    </ul>
  )
}
